import React, { useState, useEffect } from 'react';
import { Sparkles, TrendingUp, Target, Zap, ChevronRight, Briefcase, MapPin, DollarSign, Clock } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { analyzeJobMatches } from '../../../services/openaiService';

const AIJobMatching = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const [matchedJobs, setMatchedJobs] = useState([]);
  const [matchingStats, setMatchingStats] = useState(null);
  const [error, setError] = useState(null);

  const mockMatchedJobs = [
  {
    id: 1,
    title: 'Frontend Developer',
    company: 'Tech Innovations Pvt Ltd',
    location: 'Bangalore, Karnataka',
    type: 'Full-time',
    salary: '₹6-8 LPA',
    matchScore: 95,
    matchReasons: [
    'React.js expertise matches requirement',
    'Portfolio projects align with role',
    'Location preference match',
    'Salary expectation aligned'],

    skillsMatched: ['React', 'JavaScript', 'Tailwind CSS', 'Git'],
    postedDate: '2 days ago',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1448e2f4d-1766615520167.png",
    logoAlt: 'Modern tech company office building with glass facade'
  },
  {
    id: 2,
    title: 'UI/UX Designer',
    company: 'Creative Solutions India',
    location: 'Mumbai, Maharashtra',
    type: 'Full-time',
    salary: '₹5-7 LPA',
    matchScore: 89,
    matchReasons: [
    'Design portfolio highly relevant',
    'Figma proficiency matches need',
    'Experience level appropriate',
    'Industry preference aligned'],

    skillsMatched: ['Figma', 'Adobe XD', 'User Research', 'Prototyping'],
    postedDate: '1 day ago',
    logo: "https://images.unsplash.com/photo-1622380751376-6224be5656b7",
    logoAlt: 'Contemporary corporate building with geometric architecture'
  },
  {
    id: 3,
    title: 'Full Stack Developer Intern',
    company: 'StartUp Hub Technologies',
    location: 'Remote',
    type: 'Internship',
    salary: '₹20,000/month',
    matchScore: 92,
    matchReasons: [
    'Full-stack skills perfectly aligned',
    'Remote work preference match',
    'Startup culture interest aligned',
    'Learning goals match growth opportunity'],

    skillsMatched: ['React', 'Node.js', 'MongoDB', 'REST APIs'],
    postedDate: '3 hours ago',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_177ecc407-1767383843539.png",
    logoAlt: 'Professional office workspace with modern furniture'
  },
  {
    id: 4,
    title: 'Data Analyst',
    company: 'DataTech Solutions',
    location: 'Pune, Maharashtra',
    type: 'Full-time',
    salary: '₹5-6 LPA',
    matchScore: 84,
    matchReasons: [
    'Python and SQL skills match',
    'Analytics projects demonstrate capability',
    'Educational background relevant',
    'Career goals aligned with role'],

    skillsMatched: ['Python', 'SQL', 'Excel', 'Data Visualization'],
    postedDate: '5 days ago',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_189b74775-1766844247012.png",
    logoAlt: 'Modern data center with servers and technology infrastructure'
  }];


  useEffect(() => {
    const performAIMatching = async () => {
      try {
        setIsAnalyzing(true);
        setError(null);

        // Simulate user profile (in production, fetch from user context/state)
        const userProfile = {
          skills: ['React', 'JavaScript', 'Node.js', 'Python', 'SQL'],
          experience: '3',
          preferredLocation: 'Remote',
          preferredJobType: 'Full-time',
          expectedSalary: '80000'
        };

        // Simplified job listings for AI analysis
        const jobListings = mockMatchedJobs?.map((job) => ({
          id: job?.id,
          title: job?.title,
          company: job?.company,
          location: job?.location,
          type: job?.type,
          salary: job?.salary,
          requiredSkills: job?.skillsMatched,
          experienceRequired: '2'
        }));

        // Call matching service (uses client-side algorithm in browser)
        const aiResults = await analyzeJobMatches(userProfile, jobListings);

        // Merge AI results with full job data
        const enhancedJobs = aiResults?.matchedJobs?.map((aiJob) => {
          const fullJob = mockMatchedJobs?.find((j) => j?.id === aiJob?.jobId);
          return {
            ...fullJob,
            matchScore: aiJob?.matchScore,
            matchReasons: aiJob?.matchReasons,
            skillsMatched: aiJob?.skillsMatched
          };
        })?.sort((a, b) => b?.matchScore - a?.matchScore);

        setMatchingStats({
          totalJobsAnalyzed: aiResults?.totalJobsAnalyzed,
          matchesFound: aiResults?.matchesFound,
          averageMatchScore: aiResults?.averageMatchScore,
          processingTime: aiResults?.processingTime
        });

        setMatchedJobs(enhancedJobs || []);
      } catch (err) {
        console.error('AI Job Matching Error:', err?.message);
        setError('Unable to analyze jobs at this time. Please try again later.');
        // Keep showing mock data on error
        setMatchedJobs(mockMatchedJobs);
      } finally {
        setIsAnalyzing(false);
      }
    };

    performAIMatching();
  }, []);

  const getMatchScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-blue-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const getMatchScoreBg = (score) => {
    if (score >= 90) return 'bg-green-50 border-green-200';
    if (score >= 80) return 'bg-blue-50 border-blue-200';
    if (score >= 70) return 'bg-yellow-50 border-yellow-200';
    return 'bg-gray-50 border-gray-200';
  };

  if (isAnalyzing) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex flex-col items-center justify-center space-y-6">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            <Sparkles className="w-8 h-8 text-blue-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">AI Matching in Progress</h3>
            <p className="text-gray-600">Analyzing your profile against 1,247 active job listings...</p>
            <div className="mt-4 space-y-2 text-sm text-gray-500">
              <p className="flex items-center justify-center gap-2">
                <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></span>
                Evaluating skills and qualifications
              </p>
              <p className="flex items-center justify-center gap-2">
                <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></span>
                Calculating compatibility scores
              </p>
              <p className="flex items-center justify-center gap-2">
                <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></span>
                Ranking best matches
              </p>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="space-y-6">
      {/* AI Matching Stats */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">AI Matching Complete</h3>
            <p className="text-sm text-gray-600">Found {matchingStats?.matchesFound} highly compatible opportunities</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Jobs Analyzed</p>
            <p className="text-2xl font-bold text-gray-900">{matchingStats?.totalJobsAnalyzed}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Matches Found</p>
            <p className="text-2xl font-bold text-blue-600">{matchingStats?.matchesFound}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Avg Match Score</p>
            <p className="text-2xl font-bold text-green-600">{matchingStats?.averageMatchScore}%</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Processing Time</p>
            <p className="text-2xl font-bold text-purple-600">{matchingStats?.processingTime}</p>
          </div>
        </div>
      </div>

      {/* Matched Jobs List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Top Matches for You</h3>
          <Button variant="outline" size="sm">
            View All Matches
          </Button>
        </div>

        {matchedJobs?.map((job) =>
        <div key={job?.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Job Info */}
              <div className="flex-1">
                <div className="flex items-start gap-4 mb-4">
                  <img
                  src={job?.logo}
                  alt={job?.logoAlt}
                  className="w-14 h-14 rounded-lg object-cover border border-gray-200" />

                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">{job?.title}</h4>
                    <p className="text-gray-700 font-medium mb-2">{job?.company}</p>
                    <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {job?.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-4 h-4" />
                        {job?.type}
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4" />
                        {job?.salary}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {job?.postedDate}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Skills Matched */}
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Matching Skills:</p>
                  <div className="flex flex-wrap gap-2">
                    {job?.skillsMatched?.map((skill, idx) =>
                  <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full border border-blue-200">
                        {skill}
                      </span>
                  )}
                  </div>
                </div>

                {/* Match Reasons */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Why this matches:</p>
                  <ul className="space-y-1">
                    {job?.matchReasons?.slice(0, 3)?.map((reason, idx) =>
                  <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                        <Target className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        {reason}
                      </li>
                  )}
                  </ul>
                </div>
              </div>

              {/* Match Score & Actions */}
              <div className="lg:w-48 flex flex-col items-center justify-between border-t lg:border-t-0 lg:border-l border-gray-200 pt-4 lg:pt-0 lg:pl-6">
                <div className="text-center mb-4">
                  <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full border-4 ${getMatchScoreBg(job?.matchScore)}`}>
                    <div>
                      <p className={`text-3xl font-bold ${getMatchScoreColor(job?.matchScore)}`}>{job?.matchScore}</p>
                      <p className="text-xs text-gray-600">Match</p>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-center gap-1 text-sm text-green-600">
                    <TrendingUp className="w-4 h-4" />
                    <span>Excellent Fit</span>
                  </div>
                </div>

                <div className="w-full space-y-2">
                  <Button className="w-full" size="sm">
                    Apply Now
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                  <Button variant="outline" className="w-full" size="sm">
                    View Details
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Re-run Matching */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
        <Zap className="w-8 h-8 text-blue-600 mx-auto mb-3" />
        <h4 className="text-lg font-semibold text-gray-900 mb-2">Update Your Matches</h4>
        <p className="text-gray-600 mb-4">Profile updated? Re-run AI matching to find new opportunities</p>
        <Button variant="outline">
          <Sparkles className="w-4 h-4 mr-2" />
          Re-run AI Matching
        </Button>
      </div>
    </div>);

};

export default AIJobMatching;